import { z } from "zod";
//#region src/app-fixture-config.ts
/**
* The app fixture config merge route: the wire contract (plan 2026-10-12-app-config-merge-route).
*
* An app's config is one row per (app, fixture) in `release.app_fixture_configs`, app-global. The
* row carries `configEtag`, a random 16-hex tag. Release-engine writes a new tag on every change
* of the stored map. A write that leaves the map equal keeps the tag. The tag carries no
* information about the values (plan 2026-10-13-followups-config-scim, decision 2). Four routes of release-engine
* read and write the row under `/v1/releases/apps/:appId/fixtures/:fixtureId/config`:
*
* - `GET` answers {@link AppFixtureConfig}. Its `ETag` response header is a hash of the answer
*   without `configValues`, for `If-None-Match`; `configEtag` stands for the values in it. That
*   header differs from the quoted `configEtag`.
* - `PUT` replaces the whole map, and `DELETE` removes the row. Each takes an optional `If-Match`:
*   with none, the write is last-write-wins; with a stale tag, 412. The `PUT` `ETag` header carries
*   the quoted `configEtag`.
* - `PATCH` sets and unsets named keys and keeps every other key (D2, D3). The caller must send
*   `If-Match` with the `configEtag` body member it read, quoted (D4). The `GET` `ETag` header is
*   the wrong value for `If-Match`. The `PATCH` `ETag` header carries the new quoted `configEtag`. A stale tag answers 412 with
*   {@link AppFixtureConfigPreconditionFailedBody}, so of two writers with one tag, one wins (D5).
*
* The route, the `@zion/sdk-node` client, the `zion app config` CLI and the two MCP tools import
* these shapes, so one contract binds all four (D9). Every schema here carries no `.transform()`,
* so `createOpenApiDocument()` can render it. Pure: no I/O, no Node built-in, so a browser bundle
* can import it.
*/
/**
* The tag of a fixture with no stored row: the empty string. `GET` answers it as `configEtag`, and a
* `PATCH` that creates the row sends it as `If-Match: ""` (D4).
*/
const APP_FIXTURE_CONFIG_ABSENT_ETAG = "";
/** The `error` code of the 412 body: the `If-Match` tag differs from the stored tag (D4). */
const APP_FIXTURE_CONFIG_PRECONDITION_FAILED = "precondition_failed";
/** The `error` code of the 428 body: the `PATCH` carried no `If-Match` header (D4). */
const APP_FIXTURE_CONFIG_PRECONDITION_REQUIRED = "precondition_required";
/** The `error` code of the 400 body for an `If-Match` value that is not one strong tag (D4). */
const APP_FIXTURE_CONFIG_INVALID_IF_MATCH = "invalid_if_match";
/**
* The `error` code of the 400 body for a `PATCH` whose `set` names a key that the fixture declares
* in `config.secrets` (D6). The body keeps the `PUT`'s message beside the code. The code tells the
* MCP requester that the route refused the write and applied nothing.
*/
const APP_FIXTURE_CONFIG_SECRET_KEY_REFUSED = "secret_key_refused";
/**
* The longest `If-Match` header {@link parseIfMatch} reads, in characters, spaces and quotes
* included. A stored tag is 16 hex characters, so the bound leaves room. {@link parseIfMatch} reads
* the length first and refuses a longer header before any other work. The longest tag value is
* two characters less, for the quotes ({@link entityTagValueSchema}).
*/
const IF_MATCH_MAX_LENGTH = 256;
/**
* One entity tag value without its quotes: zero or more `etagc` characters of RFC 9110 section
* 8.8.3 (printable ASCII without the double quote, 0x21 and 0x23 to 0x7E), at most
* `IF_MATCH_MAX_LENGTH - 2` (254) characters, so the quoted form fits the `If-Match` bound.
*
* The callers: the `currentEtag` of the 412 body, the `ifMatch` argument of the MCP tool
* `patch_app_fixture_config`, and the `currentEtag` the MCP requester admits from an upstream
* error body. A stored tag (16 hex characters) and the empty tag pass. A value with a quote, a
* space, a CR, an LF or a character outside printable ASCII is refused, so a quoted value is
* always one strong tag and no header line can break. No `.transform()`, so
* `createOpenApiDocument()` can render it.
*/
const entityTagValueSchema = z.string().max(254).regex(/^[\x21\x23-\x7E]*$/);
/**
* The key names that a `PATCH` refuses in `set` and in `unset`: the names of parts of a JavaScript
* object. A record parse drops an own `__proto__` key without an error (zod 4.4.3 skips it before
* the key schema runs), so a write of it would answer success and store nothing. The ruling on push
* review 2 of plan 2026-10-12-app-config-merge-route refuses all three names, each with an issue
* that names the key.
*
* On the release-engine route, NestJS's `StandardSchemaValidationPipe` deletes an own `__proto__`,
* `prototype` and `constructor` key at every depth of the body (`stripProtoKeys`) before this
* schema runs. So the route refuses these names in `unset`, and a `set` key with one of these names
* never reaches the schema there. The CLI and the MCP tool parse the arguments themselves and
* refuse the names in both lists.
*/
const APP_FIXTURE_CONFIG_RESERVED_KEYS = [
	"__proto__",
	"constructor",
	"prototype"
];
/**
* The refusal text for one reserved key name, the same text in the route's 400, the MCP tool and
* the `zion app config` CLI. Pure.
*/
function appFixtureConfigReservedKeyMessage(key) {
	return `the key '${key}' is refused: __proto__, constructor and prototype name parts of a JavaScript object, so no config key may use them`;
}
const RESERVED_KEY_SET = new Set(APP_FIXTURE_CONFIG_RESERVED_KEYS);
/** The refusal text for the empty key in `set` and in `unset` (D3 amended). */
const EMPTY_KEY_MESSAGE = "a config key must be one character at least";
/**
* The `set` map of a `PATCH`: key to value. The `PATCH` stores strings only, and a key is one
* character at least (D3 amended), as an `unset` key is, so a `PATCH` cannot store a key that no
* `PATCH` can remove.
*
* The empty key is refused with one issue at its path (`a config key must be one character at
* least`), and a key in {@link APP_FIXTURE_CONFIG_RESERVED_KEYS} with one issue per key, at the
* path of that key, with {@link appFixtureConfigReservedKeyMessage}. The check reads the own keys
* of the raw input, because the record parse drops `__proto__` before any refinement sees it, and
* from zod 4.6 an intersection reports a record key refusal only when both members refuse the key.
* Each of these issues aborts, so the body rules of {@link appFixtureConfigPatchBodySchema} do not
* also read the map with the refused key gone. The check is the right member of an intersection
* with the record, so it runs no transform: OpenAPI renders the map as `allOf` with the record in
* both the input and the output view, and the MCP tool list shows the record. The
* `patch_app_fixture_config` arguments use this schema, so the tool refuses what the route refuses
* before the MCP SDK parse can drop the key.
*/
const appFixtureConfigSetMapSchema = z.intersection(z.record(z.string().min(1), z.string()), z.unknown().superRefine((raw, ctx) => {
	if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return;
	for (const key of Object.keys(raw)) if (key === "") ctx.addIssue({
		code: "custom",
		message: EMPTY_KEY_MESSAGE,
		path: [key],
		continue: false
	});
	else if (RESERVED_KEY_SET.has(key)) ctx.addIssue({
		code: "custom",
		message: appFixtureConfigReservedKeyMessage(key),
		path: [key],
		continue: false
	});
}));
/**
* The `unset` list of a `PATCH`: key names, each one character at least (D3 amended). The empty
* key is refused at its index with the text of the empty `set` key (`a config key must be one
* character at least`), as a `too_small` issue so the JSON Schema keeps `minLength: 1`. A key in
* {@link APP_FIXTURE_CONFIG_RESERVED_KEYS} is refused with {@link appFixtureConfigReservedKeyMessage}
* at its index. Each of these issues aborts, as in {@link appFixtureConfigSetMapSchema}, so the body
* rules of {@link appFixtureConfigPatchBodySchema} do not also run.
*/
const appFixtureConfigUnsetListSchema = z.array(z.string().min(1, {
	message: EMPTY_KEY_MESSAGE,
	abort: true
}).superRefine((key, ctx) => {
	if (RESERVED_KEY_SET.has(key)) ctx.addIssue({
		code: "custom",
		message: appFixtureConfigReservedKeyMessage(key),
		continue: false
	});
}));
/**
* The `PATCH` request body (D3).
*
* - `set`: the keys to write, each to its new value. A key absent from the stored map is added.
* - `unset`: the keys to remove. A key absent from the stored map is a no-op (D6).
*
* The body must name at least one key across both lists. A key named in both lists is refused,
* because the result would depend on an order the caller cannot state. The object is `.strict()`,
* so an unknown member is refused. The route answers 400 for every refusal.
*
* A key in `set` or in `unset` must be one character at least (D3 amended). The schema refuses
* the empty key in both lists, and each key in {@link APP_FIXTURE_CONFIG_RESERVED_KEYS} with an
* issue that names it. That is the one key rule this route adds to the rules of the `PUT`,
* which has no key grammar. The route also refuses a `set` key that the fixture declares in
* `config.secrets` (D6). That check needs the stored manifest, so the service runs it.
*/
const appFixtureConfigPatchBodySchema = z.object({
	set: appFixtureConfigSetMapSchema.optional(),
	unset: appFixtureConfigUnsetListSchema.optional()
}).strict().superRefine((body, ctx) => {
	const setKeys = Object.keys(body.set ?? {});
	const unsetKeys = body.unset ?? [];
	if (setKeys.length === 0 && unsetKeys.length === 0) {
		ctx.addIssue({
			code: "custom",
			message: "the body must name at least one key in set or unset",
			path: []
		});
		return;
	}
	const setKeySet = new Set(setKeys);
	const both = [...new Set(unsetKeys.filter((k) => setKeySet.has(k)))].sort();
	if (both.length > 0) ctx.addIssue({
		code: "custom",
		message: `a key must not be in both set and unset: ${both.join(", ")}`,
		path: ["unset"]
	});
});
/**
* The config of one (app, fixture), as `GET`, `PUT` and `PATCH` answer it.
*
* - `configValues`: the stored map. A row that another path wrote can hold a value that is not a
*   string, so the value type is `unknown`. The `PUT` and the `PATCH` write strings only.
* - `configEtag`: the tag of the stored map, or {@link APP_FIXTURE_CONFIG_ABSENT_ETAG} when the
*   fixture has no row. A `PATCH`, and a conditional `PUT` or `DELETE`, sends this body member,
*   quoted, as `If-Match`. The `PUT` and
*   `PATCH` `ETag` response headers carry the same tag, quoted. The `GET` `ETag` header is a hash
*   of the answer without `configValues`, for `If-None-Match`, and differs from this tag.
* - The row members `id`, `appId` (the app UUID), `fixtureId`, `updatedAt` and `updatedBy` are
*   present when a row exists and absent when it does not. A `PATCH` that only unsets keys of a
*   fixture with no row writes no row, so its answer has no row members and the empty tag (D6).
* - `secretKeys`: on the `GET` and `PATCH` answers, the key names that the newest published
*   manifest of the fixture declares in `config.secrets`. The latest composition does not need to
*   list the fixture. The list is empty when that manifest declares none or when the fixture has
*   no published manifest. A stored map can still hold such a key from before the declaration. A
*   `PATCH` can unset it and cannot set it (D6). The MCP tools `get_app_fixture_config` and
*   `patch_app_fixture_config` mask the value of each of these keys (D16). `PUT` answers no
*   `secretKeys`.
*
* The object is not strict: a client parses an answer, and a new row column must not break it.
*/
const appFixtureConfigSchema = z.object({
	configValues: z.record(z.string(), z.unknown()),
	configEtag: z.string(),
	id: z.string().optional(),
	appId: z.string().optional(),
	fixtureId: z.string().optional(),
	updatedAt: z.string().optional(),
	updatedBy: z.string().nullable().optional(),
	secretKeys: z.array(z.string()).optional()
});
/**
* The 412 body of a `PATCH`, `PUT` or `DELETE` whose `If-Match` tag differs from the stored tag
* (D4; plan 2026-10-13-followups-config-scim, decision 3).
*
* `currentEtag` is the stored tag at the time of the refusal, unquoted, or the empty string when
* no row exists. It is one {@link entityTagValueSchema} value. The `ETag` response header carries
* the same tag, quoted. The route applied nothing. The caller reads the config again, decides,
* and sends the write again with the new tag. No client retries on its own (D10).
*
* The object is not strict, because the global exception filter adds `statusCode`, `timestamp`
* and `path` to every coded body.
*/
const appFixtureConfigPreconditionFailedBodySchema = z.object({
	error: z.literal(APP_FIXTURE_CONFIG_PRECONDITION_FAILED),
	message: z.string(),
	currentEtag: entityTagValueSchema
});
/**
* The `ETag` header value, and the `If-Match` value a client sends, for a stored tag: the tag in
* double quotes. `quoteEtag('')` is `""`, the tag of a fixture with no row.
*
* The input is a tag as `configEtag` carries it: 16 hex characters or the empty string. The
* function adds the quotes and checks nothing.
*/
function quoteEtag(tag) {
	return `"${tag}"`;
}
/**
* One strong entity tag of RFC 9110 section 8.8.3: a double quote, zero or more `etagc`
* characters, a double quote. `etagc` here is printable ASCII without the double quote (0x21 and
* 0x23 to 0x7E). The `obs-text` range is left out, because no stored tag holds it. Linear, with no
* nested quantifier, so it is no ReDoS surface.
*/
const STRONG_ENTITY_TAG = /^"([\x21\x23-\x7E]*)"$/;
/** True for a space or a tab, the optional white space of RFC 9110 around a field value. */
function isSpaceOrTab(code) {
	return code === 32 || code === 9;
}
/**
* The value without its leading and trailing spaces and tabs. Two index loops, each one pass at
* most, so the cost is linear in the length; a regex trim with an alternation was quadratic on a
* run of spaces and tabs inside the value (security-1).
*/
function trimSpacesAndTabs(value) {
	let start = 0;
	let end = value.length;
	while (start < end && isSpaceOrTab(value.charCodeAt(start))) start += 1;
	while (end > start && isSpaceOrTab(value.charCodeAt(end - 1))) end -= 1;
	return value.slice(start, end);
}
/**
* Read the `If-Match` request header of a `PATCH`, `PUT` or `DELETE` (D4).
*
* The caller is the release-engine route. The input is the header as Express gives it: the string,
* or `undefined` when the request has none. Node joins two `If-Match` lines with `, `, so a
* repeated header reads as a list.
*
* In order:
*
* 1. `undefined` gives `absent`.
* 2. A header longer than {@link IF_MATCH_MAX_LENGTH}, before the trim, gives `invalid` with
*    `malformed`. This check runs before any other work, so an over-long header costs one length
*    read (security-1: a 58 KB header with a run of spaces and tabs inside it).
*
* Then a linear index loop trims leading and trailing spaces and tabs, and the trimmed value goes
* on:
*
* 3. `*` gives `invalid` with `wildcard`: this route compares one tag and has no "any row" form.
* 4. A value that starts with `W/` gives `invalid` with `weak`: a weak tag never matches under the
*    strong comparison that `If-Match` uses.
* 5. One strong tag gives `tag`, with the value between the quotes: `"abc"` gives `abc`, and `""`
*    gives the empty tag.
* 6. Any other value with a comma gives `invalid` with `list`, for example `"a", "b"`.
* 7. Anything else, the empty string included, gives `invalid` with `malformed`.
*
* Pure: it throws nothing and has no side effect.
*/
function parseIfMatch(header) {
	if (header === void 0) return { kind: "absent" };
	if (header.length > 256) return {
		kind: "invalid",
		reason: "malformed"
	};
	const value = trimSpacesAndTabs(header);
	if (value === "*") return {
		kind: "invalid",
		reason: "wildcard"
	};
	if (value.startsWith("W/")) return {
		kind: "invalid",
		reason: "weak"
	};
	const strong = STRONG_ENTITY_TAG.exec(value);
	if (strong) return {
		kind: "tag",
		tag: strong[1]
	};
	if (value.includes(",")) return {
		kind: "invalid",
		reason: "list"
	};
	return {
		kind: "invalid",
		reason: "malformed"
	};
}
//#endregion
export { APP_FIXTURE_CONFIG_ABSENT_ETAG, APP_FIXTURE_CONFIG_INVALID_IF_MATCH, APP_FIXTURE_CONFIG_PRECONDITION_FAILED, APP_FIXTURE_CONFIG_PRECONDITION_REQUIRED, APP_FIXTURE_CONFIG_RESERVED_KEYS, APP_FIXTURE_CONFIG_SECRET_KEY_REFUSED, IF_MATCH_MAX_LENGTH, appFixtureConfigPatchBodySchema, appFixtureConfigPreconditionFailedBodySchema, appFixtureConfigReservedKeyMessage, appFixtureConfigSchema, appFixtureConfigSetMapSchema, appFixtureConfigUnsetListSchema, entityTagValueSchema, parseIfMatch, quoteEtag };

//# sourceMappingURL=app-fixture-config.js.map